import _ from 'underscore';
import campaignTypeTemplate from './campaign-type-template.pug';
import { Constants } from '../constants';
import { addColumnOnLevel } from '../add-column-on-level';
import { absoluteTopImpressionRate, topImpressionRate } from '../shared/columns';

export function getSearchTermGridColumnConfig({
  i18n, bingadsGridColumn, getCurrentLevel, pageContext,
}) {
  function column(options) {
    return bingadsGridColumn(_.defaults({}, options, { i18n }));
  }

  const {
    IsAvgPositionDeprecationPhase1Enabled,
    IsAvgPositionDeprecationPhase2Enabled,
    dynamic = {},
  } = pageContext.Permissions;
  const {
    PartialConversions: IsPartialConversionsEnabled,
    ColumnConversion: IsColumnConversionEnabled,
  } = dynamic;
  const usePartialConversions = IsPartialConversionsEnabled && IsColumnConversionEnabled;

  return {
    getLevelOfDetailColumns: () => {
      const level = getCurrentLevel();
      const levelOfDetailColumns = [];

      levelOfDetailColumns.push(column({
        name: 'SearchTerm',
        title: 'ColumnName_DimSearchTerm',
        sortable: true,
        type: 'string',
        helpId: 'pop_BA_Reporting_Clarity_SearchTerm',
      }));
      levelOfDetailColumns.push(column({
        name: 'KeywordName',
        title: 'ColumnName_KeywordName',
        sortable: true,
        type: 'string',
        helpId: 'pop_BA_Reporting_Clarity_Keyword',
      }));
      levelOfDetailColumns.push(column({
        name: 'OrderItemId',
        title: 'ColumnName_OrderItemId',
        sortable: true,
        type: 'string',
        helpId: 'pop_BA_Reporting_Clarity_KeywordID',
      }));

      addColumnOnLevel(column, levelOfDetailColumns);

      if (level === Constants.AccountLevel) {
        levelOfDetailColumns.push(column({
          name: 'CampaignType',
          title: 'ColumnName_DimCampaignType',
          sortable: true,
          type: 'number',
          property: {
            get(row) {
              let campaignType = null;

              switch (row.CampaignType) {
                case 1:
                  campaignType = i18n.getString('Dimension_CampaignType_0');
                  break;
                case 3:
                  campaignType = i18n.getString('Dimension_CampaignType_1');
                  break;
                case 4:
                  campaignType = i18n.getString('Dimension_CampaignType_2');
                  break;
                case 6:
                  campaignType = i18n.getString('Dimension_CampaignType_3');
                  break;
                default:
                  break;
              }

              return {
                campaignType,
              };
            },
            key: 'CampaignType',
          },
          template: campaignTypeTemplate,
          helpId: 'pop_BA_Reporting_Clarity_CampaignType',
        }));
      }

      return levelOfDetailColumns;
    },
    getPerfColumns: () => _.compact([
      column({
        name: 'Impressions',
        title: 'ColumnName_Impressions',
        sortable: true,
        type: 'number',
        format: 'integer',
        helpId: 'Page_CampaignGrids_Impr',
      }),
      column({
        name: 'Clicks',
        title: 'ColumnName_Clicks',
        sortable: true,
        type: 'number',
        format: 'integer',
        helpId: 'Page_CampaignGrids_Clicks',
      }),
      column({
        name: 'CTR',
        title: 'ColumnName_ClickThruRate',
        sortable: true,
        type: 'number',
        format: 'percentage-value',
        helpId: 'Page_CampaignGrids_CTR',
      }),
      column({
        name: 'AverageCPC',
        title: 'ColumnName_AverageCPC',
        sortable: true,
        type: 'number',
        helpId: 'Page_CampaignGrids_AvgCPC',
      }),
      column({
        name: 'Spend',
        title: 'ColumnName_Spend',
        sortable: true,
        type: 'number',
        helpId: 'Page_CampaignGrids_Spend',
      }),
      !IsAvgPositionDeprecationPhase2Enabled && column({
        name: 'AveragePosition',
        title: 'ColumnName_AveragePosition',
        sortable: true,
        type: 'number',
        helpId: 'Page_CampaignGrids_AvgPosition',
      }),
      IsAvgPositionDeprecationPhase1Enabled && column(topImpressionRate),
      IsAvgPositionDeprecationPhase1Enabled && column(absoluteTopImpressionRate),
    ]),
    getConversionColumns: () => {
      const conversionColumns = [
        usePartialConversions ?
          column({
            name: 'ConversionsCredit',
            title: 'ColumnName_Conversions',
            sortable: true,
            type: 'number',
            format: 'decimal',
            helpId: 'pop_BA_CampaignGrids_Conv',
          }) :
          column({
            name: 'Conversions',
            title: 'ColumnName_Conversions',
            sortable: true,
            type: 'number',
            format: 'integer',
            helpId: 'Page_CampaignGrids_Conv',
          }),
        usePartialConversions ?
          column({
            name: 'PartialConversionRate',
            title: 'ColumnName_ConversionRate',
            sortable: true,
            type: 'number',
            format: 'percentage-value',
            helpId: 'pop_BA_CampaignGrids_ConvRate',
          }) :
          column({
            name: 'ConversionRate',
            title: 'ColumnName_ConversionRate',
            sortable: true,
            type: 'number',
            format: 'percentage-value',
            helpId: 'Page_CampaignGrids_ConvRate',
          }),
        usePartialConversions ?
          column({
            name: 'PartialConversionCPA',
            title: 'ColumnName_CPA',
            sortable: true,
            type: 'number',
            format: 'decimal',
            helpId: 'Page_Reporting_Clarity_CPA',
          }) :
          column({
            name: 'CPA',
            title: 'ColumnName_CPA',
            sortable: true,
            type: 'number',
            helpId: 'Page_CampaignGrids_CPA',
          }),
        column({
          name: 'RevenueOnAdSpend',
          title: 'ColumnName_RevenueOnAdSpend',
          sortable: true,
          type: 'number',
          format: 'percentage-value',
          helpId: 'Page_CampaignGrids_ReturnAdSpend',
        }),
        column({
          name: 'AdvertiserReportedRevenue',
          title: 'ColumnName_Revenue',
          sortable: true,
          type: 'number',
          helpId: 'Page_CampaignGrids_Revenue',
        }),
        usePartialConversions ?
          column({
            name: 'AllConversionsCredit',
            title: 'ColumnName_AllConversions',
            sortable: true,
            type: 'number',
            format: 'decimal',
            helpId: 'pop_BA_CampaignGrids_AllConv',
          }) :
          column({
            name: 'AllConversions',
            title: 'ColumnName_AllConversions',
            sortable: true,
            type: 'number',
            format: 'integer',
            helpId: 'pop_BA_CampaignGrids_AllConv',
          }),
        usePartialConversions ?
          column({
            name: 'AllPartialConversionCPA',
            title: 'ColumnName_AllConversionCPA',
            sortable: true,
            type: 'number',
            format: 'decimal',
            helpId: 'pop_BA_CampaignGrids_AllConvCPA',
          }) :
          column({
            name: 'AllConversionCPA',
            title: 'ColumnName_AllConversionCPA',
            sortable: true,
            type: 'number',
            helpId: 'pop_BA_CampaignGrids_AllConvCPA',
          }),
        usePartialConversions ? column({
          name: 'AllPartialConversionRate',
          title: 'ColumnName_AllConversionRate',
          sortable: true,
          type: 'number',
          format: 'percentage-value',
          helpId: 'pop_BA_CampaignGrids_AllConvRate',
        }) :
          column({
            name: 'AllConversionRate',
            title: 'ColumnName_AllConversionRate',
            sortable: true,
            type: 'number',
            format: 'percentage-value',
            helpId: 'pop_BA_CampaignGrids_AllConvRate',
          }),
        column({
          name: 'AllConversionAdvertiserReportedRevenue',
          title: 'ColumnName_AllConversionAdvertiserReportedRevenue',
          sortable: true,
          type: 'number',
          helpId: 'pop_BA_CampaignGrids_AllConvRevenue',
        }),
        column({
          name: 'AllConversionRevenueOnAdSpend',
          title: 'ColumnName_AllConversionRevenueOnAdSpend',
          sortable: true,
          type: 'number',
          helpId: 'pop_BA_CampaignGrids_AllConvROAS',
        }),
      ];

      return conversionColumns;
    },
  };
}
